/**
 * A link to a workspace file that opens it in THIS tab's workspace.
 *
 * Gate cards carry `portal_url` as `/?workspace=<path>`. A plain
 * `target="_blank"` link opened a second Portal tab for it; this hands the path
 * to the panel's opener instead (the same one the file_send cards use), so the
 * file expands in the workspace next to the chat.
 *
 * Only a plain left click is taken over. Cmd/Ctrl/Shift/middle click, a URL
 * that is not a workspace link, and a tree with no opener (a card rendered on
 * its own, e.g. in a component test) keep the browser's new-tab behaviour.
 */
import { createContext, useContext, type ReactNode } from 'react';
import { workspacePathFromUrl } from '../../../hooks/useOpenWorkspaceFile';

export const OpenWorkspaceFileContext = createContext<((path: string) => void) | null>(null);

export function WorkspaceFileLink({
  href,
  className,
  children,
}: {
  href: string;
  className?: string;
  children: ReactNode;
}) {
  const openFile = useContext(OpenWorkspaceFileContext);
  const path = workspacePathFromUrl(href);
  return (
    <a
      className={className}
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => {
        if (!openFile || !path) return;
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        openFile(path);
      }}
    >
      {children}
    </a>
  );
}
